import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { LAYERS } from 'emergency_data/src/components/Map/useDeckGL/Axon/useAxonGLNext.js';
import { hidePinnedPopover } from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import {
  addRecentSearch,
  clearRecentSearches,
  removeRecentSearch,
  selectActiveLayers,
  selectGeocodeService,
  selectRecentSearches,
} from '@rsos/reduxDataStore';
import {
  clearHighlightedFeature,
  clearSelectedLocation,
} from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import useCenterPointCurrentView from '@rsos/utils/useCenterPointCurrentView';
import type { RootState } from '../../../../app';
import callQueueSelectorNext from '../../../../selectors/callQueueSelectorNext';
import { createDatasetsSelector } from '../../../../utils/search';
import { AXON } from '../../../../utils/search/constants';
import type {
  AdrDataByTopic,
  AlertsById,
  AllTextMessages,
  CallQueue,
  MapCenter,
  ReifiedLayer,
  SearchDataset,
  SpatialLayer,
  TranscriptionsByCallId,
} from './GlobalMapSearch.types';
import type { SearchResultItem } from './GlobalMapSearch.types';
import { GIS_SEARCH_MIN_LENGTH } from './GlobalMapSearch.utils';
import MapSearchDropdown from './MapSearchDropdown';
import RecentSearchesDropdown from './RecentSearchesDropdown';
import {
  buildRecentEntryFromPrepared,
  type RecentSearchEntry,
} from './recentSearchEntry';
import {
  REGULAR_TYPEAHEAD_DEBOUNCE_MS,
  shouldRunRegularTypeahead,
} from './regularTypeahead';
import { selectActiveDynamicLayers } from './search/dynamic/selectActiveDynamicLayers';
import { selectActiveStaticLayers } from './search/static/selectActiveStaticLayers';
import useAdrSearch from './useAdrSearch';
import useExecuteSearch from './useExecuteSearch';
import type { PreparedItem } from './useGroupedResults';
import useGroupedResults from './useGroupedResults';
import useLatLongSearch from './useLatLongSearch';
import useLayerSuggestion from './useLayerSuggestion';
import useLeiSearch, { isCallerInQueue } from './useLeiSearch';
import useMedicAlertSearch from './useMedicAlertSearch';
import useResultActions from './useResultActions';
import useWhat3WordsSearch from './useWhat3WordsSearch';

const axonDatasetsSelector = createDatasetsSelector([AXON]);

/**
 * Imperative surface the parent shell talks to. Keeps the regular
 * workflow's state private to this component while still letting the
 * shared `<Input>` route key events / change events / clear / submit
 * actions through.
 */
export interface RegularExperienceHandle {
  /** Forwarded keystrokes — only consumed while regular mode is active. */
  onKeyDown: (e: React.KeyboardEvent) => void;
  /**
   * Forwarded onChange. Performs the wipe (clear results, abort in-
   * flight search, drop pinned popover/highlight) when the new value
   * is plain text. No-ops on slash inputs so typing `/transcript hi`
   * doesn't tear down whatever the user had on screen before.
   */
  onChange: (next: string) => void;
  /** Wipe regular state (called from the shell's clear button). */
  onClear: () => void;
  /**
   * Close the regular dropdown. Used by the shell's click-outside
   * handler and by a successful slash submit (the regular dropdown
   * may have been left open from before slash mode).
   */
  onClose: () => void;
  /**
   * Forwarded focus. Resets the keyboard cursor (so an old hover-
   * index from the results list doesn't pre-highlight a stale row
   * when the recents surface re-opens) and re-opens the dropdown if
   * we still have results from a prior search.
   */
  onFocus: () => void;
}

interface RegularSearchExperienceProps {
  searchTerm: string;
  setSearchTerm: (next: string) => void;
  /**
   * True while the input starts with `/`. The component still mounts
   * (so its hooks/state survive a round-trip through slash mode) but
   * skips the dropdown render.
   */
  isHidden: boolean;
  isFocused: boolean;
  isFocusedRef: React.MutableRefObject<boolean>;
  inputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Owns the entire regular (non-slash) search workflow end-to-end:
 * selectors, search engine, recents, layer suggestion, both dropdown
 * surfaces, and all keyboard handling for those surfaces. Shares only
 * the input element and focus refs with the shell — every other piece
 * of state and every effect is local.
 */
const RegularSearchExperience = forwardRef<
  RegularExperienceHandle,
  RegularSearchExperienceProps
>(function RegularSearchExperience(
  { searchTerm, setSearchTerm, isHidden, isFocused, isFocusedRef, inputRef },
  ref,
) {
  const dispatch = useDispatch();
  // Read transcriptions/messages/alerts from the store at search time
  // to avoid any render-batching window between a token dispatch and
  // the next render.
  const store = useStore();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // --- Local state ---
  const [isOpen, setIsOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const recentSearches = useSelector(
    selectRecentSearches,
  ) as RecentSearchEntry[];

  // --- Selectors ---
  const activeLayers = useSelector(selectActiveLayers) as ReifiedLayer[];
  const mapCenter: MapCenter = useCenterPointCurrentView();
  const jurisdictionBoundary = useSelector(
    (state: RootState) =>
      (
        state as unknown as {
          geofences?: { currentGeofence?: { expanded_polygon?: unknown } };
        }
      ).geofences?.currentGeofence?.expanded_polygon ?? null,
  );
  const axonDatasets = useSelector(axonDatasetsSelector) as
    | SearchDataset[]
    | undefined;
  const activeStaticLayers = useSelector(
    selectActiveStaticLayers,
  ) as SpatialLayer[];
  const activeDynamicLayers = useSelector(
    selectActiveDynamicLayers,
  ) as SpatialLayer[];
  const axonLayerData = useSelector((state: RootState) => {
    const byId = state.schemas?.byId as Record<string, unknown> | undefined;
    return byId?.[LAYERS.AXON_BODY_3];
  });
  const geocodeService = useSelector(selectGeocodeService) as {
    gis_service_url?: string;
    token?: string | null;
  } | null;
  // `callQueueSelectorNext` is the canonical view of the call queue for
  // the new map search experience: ANI/ALI-filtered per the user's
  // permissions, merged with outbound text-from-911 conversations, and
  // sorted newest-first. Reading the raw `state.emergencyCalls.callQueue`
  // would skip those filters and surface calls the user shouldn't see /
  // miss text-only conversations the rest of the JV already shows.
  const callQueue = useSelector(callQueueSelectorNext) as CallQueue;

  // --- Refs mirroring selector values for stable access inside async work ---
  const activeLayersRef = useRef(activeLayers);
  activeLayersRef.current = activeLayers;
  const axonDatasetsRef = useRef(axonDatasets);
  axonDatasetsRef.current = axonDatasets;
  const activeStaticLayersRef = useRef(activeStaticLayers);
  activeStaticLayersRef.current = activeStaticLayers;
  const activeDynamicLayersRef = useRef(activeDynamicLayers);
  activeDynamicLayersRef.current = activeDynamicLayers;
  const mapCenterRef = useRef(mapCenter);
  mapCenterRef.current = mapCenter;
  const jurisdictionBoundaryRef = useRef<unknown>(jurisdictionBoundary);
  jurisdictionBoundaryRef.current = jurisdictionBoundary;
  const callQueueRef = useRef(callQueue);
  callQueueRef.current = callQueue;

  // --- Search engine ---
  const {
    results,
    setResults,
    isSearching,
    setIsSearching,
    abortRef,
    executeSearch,
  } = useExecuteSearch({
    mapCenterRef,
    jurisdictionBoundaryRef,
    isFocusedRef,
    setIsOpen,
    setCursor,
  });

  const what3words = useWhat3WordsSearch();
  const latLong = useLatLongSearch();
  const medicAlert = useMedicAlertSearch();
  const adr = useAdrSearch();
  const lei = useLeiSearch();

  // LEI fires on submit (Enter) for any USA phone-
  // shaped term that doesn't already match an active call. Hoisted
  // out of `triggerSearch` so the Enter handler can also call it on
  // the early-return paths (recents pick, dropdown-row pick), where
  // `triggerSearch` would otherwise be skipped — without that the
  // user's "Enter on phone number" submit silently picks the first
  // GIS suggestion and never pulls LEI location data.
  const kickoffLeiIfApplicable = useCallback(
    (term: string) => {
      const leiCallerID = lei.parse(term);
      if (leiCallerID && !isCallerInQueue(leiCallerID, callQueueRef.current)) {
        void lei.kickoff(leiCallerID);
      }
    },
    [lei],
  );

  // Inject any resolved RAD-e lookups as first-class search rows so
  // they participate in grouping, the filter pills, and keyboard
  // navigation alongside the regular results. Pending / empty / error
  // states are handled below via the combined searching flag and the
  // existing empty state — no row is rendered until the fetch lands
  // with at least one item.
  const augmentedResults = useMemo<SearchResultItem[]>(() => {
    const extras: SearchResultItem[] = [];
    if (
      medicAlert.lookup &&
      medicAlert.lookup.status === 'success' &&
      medicAlert.lookup.data &&
      medicAlert.lookup.data.length > 0
    ) {
      extras.push({
        type: 'rade-maf',
        data: { id: medicAlert.lookup.id },
      });
    }
    if (
      adr.lookup &&
      adr.lookup.status === 'success' &&
      adr.lookup.data &&
      adr.lookup.data.length > 0
    ) {
      extras.push({ type: 'rade-adr', data: { id: adr.lookup.id } });
    }
    if (extras.length === 0) return results;
    // Append RAD-e rows into the result stream for grouping. Display
    // order is driven by `CATEGORY_PRIORITY_ORDER` in
    // `useGroupedResults`, not by insertion position here.
    return [...results, ...extras];
  }, [results, medicAlert.lookup, adr.lookup]);

  // Surface a combined "anything in flight" flag to the dropdown so
  // the "Searching…" indicator stays visible until BOTH the regular
  // text search AND every kicked-off RAD-e lookup have settled. This
  // is also what gates the "No data found" empty state — we don't
  // want to flash an empty-state while a RAD-e lookup is still
  // resolving.
  const combinedIsSearching =
    isSearching ||
    medicAlert.lookup?.status === 'pending' ||
    adr.lookup?.status === 'pending';

  const grouped = useGroupedResults(augmentedResults, mapCenter, activeFilter);
  const { visibleItems } = grouped;
  const flatCount = visibleItems.length;

  // Slash commands are explicit user intent — `/transcript foo` is
  // never going to be a layer name, and we don't want the AI
  // classifier firing (or the alias matcher running) against the
  // command text. Pass empty string to the hook so it short-circuits
  // to "no suggestion" while the user is in slash mode. The
  // experience itself is `isHidden` then anyway, but the hook still
  // runs (component stays mounted to preserve regular state across
  // mode switches), so the gate has to be at the input boundary.
  const layerSuggestionQuery = searchTerm.startsWith('/') ? '' : searchTerm;
  const {
    suggestion: layerSuggestion,
    isActivating: isActivatingLayer,
    activate: activateSuggestedLayer,
    dismissSuggestion,
  } = useLayerSuggestion(layerSuggestionQuery);

  const { handleResultClick: dispatchResultAction } = useResultActions({
    axonLayerData,
  });

  // --- Click handlers ---
  const handleResultClick = useCallback(
    (prepared: PreparedItem) => {
      dispatchResultAction(prepared.item);
      // Capture the selection in the persisted recent-search list so
      // the user can re-open this exact record (with the right icon,
      // label, and action) from the recents surface — even across page
      // reloads. Done before closing so an unmounted dropdown can't
      // race the dispatch.
      dispatch(
        addRecentSearch(buildRecentEntryFromPrepared(searchTerm, prepared)),
      );
      // Close the dropdown and drop focus from the search bar so the
      // user can interact with whatever just got selected (call pane,
      // map pin, etc.) without having to dismiss the dropdown manually.
      setIsOpen(false);
      setCursor(-1);
      isFocusedRef.current = false;
      inputRef.current?.blur();
    },
    [dispatchResultAction, dispatch, searchTerm, isFocusedRef, inputRef],
  );

  // --- Debounced typeahead timer ---
  // Holds the pending auto-search timeout so we can flush it on an
  // explicit submit (Enter) and cancel it on clear,
  // unmount, or transition into slash-command mode.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDebouncedSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  // --- Trigger search ---
  // `overrideTerm` lets recent-search clicks dispatch a search
  // synchronously with the freshly-selected query rather than waiting
  // for the closure to pick up a `setSearchTerm` from the same render.
  //
  // `options.fromTypeahead` distinguishes the debounced auto-fire from
  // an explicit submit (Enter / recents click). On
  // typeahead we skip the disruptive shortcuts (W3W, lat-long, both
  // teleport the map and blur the input) and the recent-search
  // persistence (every 500ms pause would otherwise pollute history
  // with intermediate strings). MAF/ADR/LEI background lookups still
  // run because they self-gate on phone/digit shapes and behave fine
  // under the debounce.
  const triggerSearch = useCallback(
    (overrideTerm?: string, options?: { fromTypeahead?: boolean }) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      const fromTypeahead = options?.fromTypeahead === true;
      const term = overrideTerm ?? searchTerm;
      abortRef.current?.abort();
      dispatch(clearHighlightedFeature());
      setActiveFilter(null);
      if (term.length < GIS_SEARCH_MIN_LENGTH) {
        setResults([]);
        setIsSearching(false);
        return;
      }
      if (!fromTypeahead) {
        // what3words shortcut: only triggers on the strict
        // `word.word.word` shape (two literal periods, letters only, no
        // spaces). The strict matcher exists so terms like
        // `chuck e. cheese` or a parcel-owner name with a single period
        // don't get hijacked into a W3W lookup before the regular search
        // path runs.
        const w3w = what3words.parse(term);
        if (w3w) {
          setResults([]);
          setIsSearching(false);
          setIsOpen(false);
          inputRef.current?.blur();
          dispatch(addRecentSearch(term));
          void what3words.search(w3w);
          return;
        }
        // Lat/long shortcut: detect strict `lat,lng` coordinate input
        // and pin that point directly (with reverse-geocoded label when
        // available) instead of sending the query through the text-search
        // orchestrator.
        const latLongPoint = latLong.parse(term);
        if (latLongPoint) {
          setResults([]);
          setIsSearching(false);
          setIsOpen(false);
          inputRef.current?.blur();
          dispatch(addRecentSearch(term));
          void latLong.search(latLongPoint);
          return;
        }
      }
      // RAD-e background lookups: fire in parallel with the regular
      // text search rather than short-circuiting it. Each lookup
      // resolves into its own `radeSlice` channel and is surfaced as
      // a "Additional Data" row in the dropdown once data lands. The
      // user clicks the row to open the RAD-e map card — we no
      // longer auto-open it on submit.
      //
      //   - MAF (MedicAlert): any all-digit term + MA access.
      //   - ADR (phone): term that passes `isValidInternationalPhoneNumber`
      //     (libphonenumber-js, US default), ungated to mirror the legacy
      //     ConnectedUniversalSearch ADR path.
      //
      // Both can fire on the same input (a 10-digit string with MA
      // access matches both shapes); the dropdown will then render
      // two rows, one per lookup, when each resolves with data.
      const mafId = medicAlert.parse(term);
      if (mafId && medicAlert.hasAccess) {
        medicAlert.kickoff(mafId);
      } else {
        medicAlert.reset();
      }
      const adrId = adr.parse(term);
      if (adrId) {
        adr.kickoff(adrId);
      } else {
        adr.reset();
      }
      // LEI (Legacy Emergency Info) phone lookup. Runs in parallel with
      // the ADR pull on phone-shaped input, but only when the caller
      // isn't already represented in the active call queue — for
      // queued calls the regular call-result row in the dropdown
      // already owns the `setSelectedCallerID` hand-off and we don't
      // want a duplicate location pull.
      //
      // The user's input is left untouched in the search bar; the
      // LEI fetch is widened to the leading-`1` 11-digit form here
      // (`parseLeiTerm`) so a 10-digit submit still hits the LEI
      // service with the canonical caller ID.
      kickoffLeiIfApplicable(term);
      setIsSearching(true);
      if (isFocusedRef.current) setIsOpen(true);
      // Stash this query in the persisted recent-search list so the
      // pre-search dropdown can resurface it next time the user
      // focuses the bar. Done before kicking off the async work so
      // an aborted request doesn't lose the user's intent from
      // history. Skipped on typeahead auto-fires — the user hasn't
      // explicitly committed to that query yet, and otherwise every
      // 500ms pause would pollute history with intermediate strings
      // (`m`, `ma`, `mai`, `main`, `main `...).
      if (!fromTypeahead) {
        dispatch(addRecentSearch(term));
      }
      const snapshot = store.getState() as unknown as {
        transcriptions?: { calls?: TranscriptionsByCallId };
        talkjsMessages?: { allTextMessages?: AllTextMessages };
        // The slice is `incidents`, not `alerts`. See incidentsSlice.js.
        incidents?: { alerts?: AlertsById; removedAlerts?: string[] };
        // ADR data is keyed by topic (`alert://<id>`). Train car info,
        // hazmat, and other rich-payload fields land here rather than
        // on the base alert object.
        adrData?: { data?: AdrDataByTopic };
      };
      const latestTranscriptions = snapshot.transcriptions?.calls;
      const latestMessages = snapshot.talkjsMessages?.allTextMessages;
      const latestAlerts = snapshot.incidents?.alerts;
      const latestRemovedAlerts = snapshot.incidents?.removedAlerts;
      const latestAlertAdrData = snapshot.adrData?.data;
      void executeSearch({
        text: term,
        layers: activeLayersRef.current,
        axonDs: axonDatasetsRef.current,
        staticLayers: activeStaticLayersRef.current,
        dynamicLayers: activeDynamicLayersRef.current,
        callQueue: callQueueRef.current,
        transcriptions: latestTranscriptions,
        messages: latestMessages,
        alerts: latestAlerts,
        removedAlerts: latestRemovedAlerts,
        alertAdrData: latestAlertAdrData,
        serviceUrl: geocodeService?.gis_service_url,
        token: geocodeService?.token,
      });
    },
    [
      searchTerm,
      dispatch,
      executeSearch,
      geocodeService,
      store,
      abortRef,
      setResults,
      setIsSearching,
      what3words,
      latLong,
      medicAlert,
      adr,
      kickoffLeiIfApplicable,
      isFocusedRef,
      inputRef,
    ],
  );

  // --- Layer activation ---
  // Activation dispatches into redux; the resulting re-render is what
  // refreshes `activeLayersRef.current`. We stash the activated layer
  // id *before* awaiting activation so that when the redux updates
  // flush and `activeLayers` changes, the effect below already has the
  // target id to anchor on. Setting the ref *after* the await would
  // be too late — the re-render from those dispatches will have
  // already fired the effect with a stale (null) ref, and a ref
  // write doesn't trigger another render.
  const layerIdToSearchRef = useRef<string | null>(null);

  const handleActivateLayer = useCallback(async () => {
    if (!layerSuggestion) return;
    const targetId = layerSuggestion.layer.id;
    // Keep the dropdown open and show the searching dots immediately
    // so the user gets continuous feedback from click → load →
    // re-search instead of the bar momentarily going quiet.
    setIsOpen(true);
    setIsSearching(true);
    layerIdToSearchRef.current = targetId;
    await activateSuggestedLayer();
  }, [activateSuggestedLayer, layerSuggestion, setIsSearching]);

  useEffect(() => {
    const targetId = layerIdToSearchRef.current;
    if (!targetId) return;
    const target = activeLayers.find(l => l.id === targetId);
    // Wait until the layer is both active *and* fully reified —
    // `gisLayer` is only populated once `loadGISLayer.fulfilled` has
    // run and the selector has re-built the layer tree. Without this
    // guard we can fire the re-search on an intermediate render where
    // the layer is active but its underlying ArcGIS layer reference
    // hasn't been wired up yet, which is why the new layer's hits
    // never made it into the result list.
    if (!target || !target.gisLayer) return;
    layerIdToSearchRef.current = null;
    triggerSearch();
  }, [activeLayers, triggerSearch]);

  // --- Filter pills ---
  const handleToggleFilter = useCallback((cat: string) => {
    setActiveFilter(prev => (prev === cat ? null : cat));
    setCursor(-1);
  }, []);

  // --- Recent searches handlers ---
  const handleSelectRecent = useCallback(
    (entry: RecentSearchEntry) => {
      setCursor(-1);
      // Result-backed recents short-circuit straight back into the
      // original action (open the call, pan to the place, etc.) — no
      // re-search round trip. We still bump the entry to the top of
      // the list so the next focus surfaces it again, and reflect the
      // saved query in the input so the user sees what they originally
      // typed alongside the surfaced selection.
      if (entry.result) {
        setSearchTerm(entry.query);
        if (inputRef.current) inputRef.current.value = entry.query;
        dispatchResultAction(entry.result.item);
        dispatch(addRecentSearch(entry));
        setIsOpen(false);
        isFocusedRef.current = false;
        inputRef.current?.blur();
        return;
      }
      // Query-only entry: behave like the user just retyped the
      // query and submitted it.
      const term = entry.query;
      setSearchTerm(term);
      // Reflect the selection in the input synchronously so the user
      // sees the chosen query land in the bar even before the next
      // React render.
      if (inputRef.current) inputRef.current.value = term;
      triggerSearch(term);
    },
    [
      triggerSearch,
      dispatchResultAction,
      dispatch,
      setSearchTerm,
      isFocusedRef,
      inputRef,
    ],
  );

  const handleRemoveRecent = useCallback(
    (entry: RecentSearchEntry) => {
      dispatch(removeRecentSearch({ key: entry.key }));
      setCursor(-1);
    },
    [dispatch],
  );

  const handleClearAllRecents = useCallback(() => {
    dispatch(clearRecentSearches());
    setCursor(-1);
  }, [dispatch]);

  // --- Recents prefix matcher ---
  // Match the typed text against the recent-search history so the
  // user can re-open a previous record/query with a single click
  // before they've bothered to type the whole thing out. Case-
  // insensitive prefix match against either the original query or
  // (for result-backed entries) the result's display label — typing
  // "fi" surfaces both a previous "fire station 5" search and a
  // previously-clicked "Fire Hydrant 220" record. We drop entries
  // that exactly equal the current term (no value re-suggesting what
  // they already typed) and cap the surface so it never overwhelms
  // the API results below.
  const RECENT_MATCH_LIMIT = 5;
  const recentMatches = useMemo(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return recentSearches;
    const lower = trimmed.toLowerCase();
    return recentSearches
      .filter(entry => {
        const ql = entry.query.toLowerCase();
        const ll = entry.result?.label?.toLowerCase() ?? '';
        if (ql === lower) return false;
        return ql.startsWith(lower) || (ll && ll.startsWith(lower));
      })
      .slice(0, RECENT_MATCH_LIMIT);
  }, [recentSearches, searchTerm]);

  // The pre-search "recent searches" surface stays up as long as the
  // user is focused and we have something useful to show — not gated
  // on the API-search threshold. Gating on character count caused a
  // visible gap at the 3rd keystroke: the recents dropdown hid
  // because `length < MIN` flipped false, but the results dropdown
  // hadn't reopened yet (`isOpen` is reset to false on every keystroke
  // and only flips back when `useExecuteSearch` returns). Instead we
  // hand off only when there's a reason to — search is in flight, or
  // results have actually arrived. Empty input always shows recents
  // (gives the "Start searching" state to first-time users); typed
  // input shows them only while at least one prefix matches.
  const showRecents =
    isFocused &&
    !combinedIsSearching &&
    augmentedResults.length === 0 &&
    (searchTerm.length === 0 || recentMatches.length > 0);

  const recentCount = recentMatches.length;

  // The MapSearchDropdown can host an inline "Recent" section while
  // no live rows are visible, so we keep the surface mounted whenever
  // there are matching recents — even if API results haven't arrived
  // (or won't, for a typo'd query). Without this clause the user would
  // see their recent match flash on every keystroke between the
  // dropdown closing and re-opening.
  const showDropdown =
    isOpen &&
    (combinedIsSearching ||
      augmentedResults.length > 0 ||
      searchTerm.length >= GIS_SEARCH_MIN_LENGTH ||
      recentMatches.length > 0);

  // --- Keyboard handling ---
  // Keep the keyboard-selected row visible inside the dropdown.
  useEffect(() => {
    if (cursor < 0 || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(
      `[data-result-index="${cursor}"]`,
    ) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cursor]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isHidden) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        // When the recents surface is visible, Enter should fire the
        // currently highlighted recent (if any) rather than the empty-
        // string search path.
        if (showRecents && cursor >= 0 && cursor < recentCount) {
          // LEI fires here too: if the user has typed a phone number
          // and arrows onto a recent, we still want to pull location
          // data for the typed phone. No-op for non-phone terms.
          kickoffLeiIfApplicable(searchTerm);
          handleSelectRecent(recentMatches[cursor]);
          return;
        }
        // Enter inside the regular results dropdown: pick the
        // highlighted row, or — if the user hasn't arrowed onto
        // anything — auto-pick the first row instead of re-running
        // an empty search.
        if (isOpen && flatCount > 0) {
          // LEI fires here too. Without this, an Enter on a phone-
          // shaped query that happens to have any GIS suggestion
          // visible would pick the suggestion and skip `triggerSearch`
          // entirely, leaving LEI unfired.
          kickoffLeiIfApplicable(searchTerm);
          const idx = cursor >= 0 ? cursor : 0;
          const target = visibleItems[idx];
          if (target) handleResultClick(target);
          return;
        }
        // Fall-through: `triggerSearch` itself fires LEI, so we
        // don't double-call here.
        triggerSearch();
        return;
      }

      if (e.key === 'Escape') {
        setIsOpen(false);
        setCursor(-1);
        // Drop focus too so the recents surface doesn't immediately
        // re-open; users tapping Escape are signalling "done with the
        // bar".
        isFocusedRef.current = false;
        inputRef.current?.blur();
        return;
      }

      const navCount = showRecents ? recentCount : flatCount;
      if ((!isOpen && !showRecents) || navCount === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor(prev => Math.min(prev + 1, navCount - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor(prev => {
          const next = prev - 1;
          if (next < 0) {
            inputRef.current?.focus();
            return -1;
          }
          return next;
        });
      }
    },
    [
      isHidden,
      isOpen,
      showRecents,
      flatCount,
      recentCount,
      recentMatches,
      visibleItems,
      cursor,
      handleResultClick,
      handleSelectRecent,
      triggerSearch,
      kickoffLeiIfApplicable,
      searchTerm,
      isFocusedRef,
      inputRef,
    ],
  );

  // --- Imperative surface for the shell ---
  const handleChange = useCallback(
    (next: string) => {
      // Slash-command keystrokes don't touch the live search pipeline
      // — they only feed the palette / scoped-search bridge effect.
      // Tearing down search state on every command-mode keystroke
      // would clobber the dropdown the bridge is in the middle of
      // populating (e.g. `/transcript hello` would have its own
      // results wiped on the next typed character). We do still
      // cancel any pending typeahead timer so an in-flight regular
      // auto-search doesn't fire after the user has switched modes.
      if (next.startsWith('/')) {
        cancelDebouncedSearch();
        return;
      }
      // Any pending auto-search is invalidated the moment the user
      // types another character — schedule a fresh one below.
      cancelDebouncedSearch();
      if (!shouldRunRegularTypeahead(next)) {
        // Below the typeahead threshold the bar returns to its idle
        // state: stale results cleared, dropdown closed, in-flight
        // search aborted, and any prior pinned/highlighted map state
        // dropped so it can't outlive the query that produced it.
        setResults([]);
        setIsOpen(false);
        abortRef.current?.abort();
        dispatch(clearHighlightedFeature());
        dispatch(clearSelectedLocation());
        dispatch(hidePinnedPopover());
        medicAlert.reset();
        adr.reset();
        return;
      }
      // Above the threshold: abort any still-streaming run from the
      // previous query so its late-arriving sources can't keep writing
      // hits for the old text into the dropdown, then schedule a
      // debounced auto-fire of the same parity-complete `triggerSearch`
      // path Enter uses. Intentionally do NOT clear
      // `results` here — keeping the previous result list visible until
      // the new run streams in avoids a flicker between the old
      // dropdown closing and the new one opening on every keystroke.
      abortRef.current?.abort();
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        triggerSearch(next, { fromTypeahead: true });
      }, REGULAR_TYPEAHEAD_DEBOUNCE_MS);
    },
    [
      dispatch,
      abortRef,
      setResults,
      medicAlert,
      adr,
      triggerSearch,
      cancelDebouncedSearch,
    ],
  );

  const handleClear = useCallback(() => {
    cancelDebouncedSearch();
    setResults([]);
    setIsOpen(false);
    setActiveFilter(null);
    abortRef.current?.abort();
    dispatch(clearHighlightedFeature());
    dispatch(clearSelectedLocation());
    dispatch(hidePinnedPopover());
    medicAlert.reset();
    adr.reset();
  }, [dispatch, abortRef, setResults, medicAlert, adr, cancelDebouncedSearch]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCursor(-1);
  }, []);

  const handleFocus = useCallback(() => {
    setCursor(-1);
    if (results.length > 0) setIsOpen(true);
  }, [results.length]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: handleKeyDown,
      onChange: handleChange,
      onClear: handleClear,
      onClose: handleClose,
      onFocus: handleFocus,
    }),
    [handleKeyDown, handleChange, handleClear, handleClose, handleFocus],
  );

  if (isHidden) return null;

  if (showRecents) {
    return (
      <RecentSearchesDropdown
        dropdownRef={dropdownRef}
        recentSearches={recentMatches}
        query={searchTerm}
        cursor={cursor}
        onCursorChange={setCursor}
        onSelect={handleSelectRecent}
        onRemove={handleRemoveRecent}
        onClearAll={handleClearAllRecents}
      />
    );
  }

  if (!showDropdown) return null;

  return (
    <MapSearchDropdown
      dropdownRef={dropdownRef}
      isSearching={combinedIsSearching}
      query={searchTerm}
      results={augmentedResults}
      grouped={grouped}
      activeFilter={activeFilter}
      cursor={cursor}
      onCursorChange={setCursor}
      onToggleFilter={handleToggleFilter}
      onResultClick={handleResultClick}
      layerSuggestion={layerSuggestion}
      isActivatingLayer={isActivatingLayer}
      onActivateLayer={handleActivateLayer}
      onDismissSuggestion={dismissSuggestion}
      recentMatches={recentMatches}
      onSelectRecent={handleSelectRecent}
    />
  );
});

export default RegularSearchExperience;
